import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { ApiError } from '../../../lib/api';
import type {
  CreateLeadFormInput,
  LeadFormQuestion,
  LeadFormThankYou,
} from '../../../types/metaAdResources';
import {
  PREFILLED_LEAD_FORM_QUESTION_TYPES,
  emptyCustomQuestion,
  isCustomQuestionType,
  leadFormQuestionTypeLabel,
  slugifyLeadFormKey,
  validateCreateLeadFormInput,
} from '../../../lib/leadForms';
import { createLeadForm } from '../api';

const inputCls =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500';
const labelCls = 'mb-1.5 block text-sm font-medium text-slate-700';

// The type dropdown offers every prefilled type plus CUSTOM.
const QUESTION_TYPE_OPTIONS = [...PREFILLED_LEAD_FORM_QUESTION_TYPES, 'CUSTOM'];

// Common Meta completion-screen button types. The current value is folded in (so a
// duplicated form's button type isn't lost if Meta used one outside this set).
const THANK_YOU_BUTTON_TYPES = ['VIEW_WEBSITE', 'DOWNLOAD', 'CALL_BUSINESS'];

const emptyThankYou = (): LeadFormThankYou => ({
  title: null,
  body: null,
  buttonText: null,
  buttonType: null,
  websiteUrl: null,
});

// Lead Forms inline create + duplicate (Phase 2). One modal for both: `initial`
// is a blank create draft (create-from-scratch) or a fetched-form prefill
// (duplicate). The full create-accepted field set is authorable here — prefilled +
// CUSTOM questions (API key + label + helper text + multiple-choice options), the
// thank-you screen, the Open vs Higher-intent form type, and the optionals.
// Duplicate carries the source's questions / thank-you / form-type / intro card
// through. On success returns the new form id + name (the leadGenFormId), which the
// caller sets on the ad.
export function LeadFormModal({
  open,
  mode,
  accountId,
  initial,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  mode: 'create' | 'duplicate';
  accountId: string;
  initial: CreateLeadFormInput;
  onOpenChange: (open: boolean) => void;
  onCreated: (form: { id: string; name: string }) => void;
}) {
  const [draft, setDraft] = useState<CreateLeadFormInput>(initial);
  const [showErrors, setShowErrors] = useState(false);

  // Reseed each time the modal opens (the caller passes a fresh blank/prefill).
  useEffect(() => {
    if (open) {
      setDraft(initial);
      setShowErrors(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reseed on open only
  }, [open]);

  const create = useMutation({
    mutationFn: () => createLeadForm(accountId, draft),
    onSuccess: (res) => {
      onCreated({ id: res.formId, name: res.name });
      onOpenChange(false);
    },
  });

  // ---------- question builder updaters ----------
  const setQuestions = (questions: LeadFormQuestion[]) => setDraft((d) => ({ ...d, questions }));
  const updateQuestion = (index: number, patch: Partial<LeadFormQuestion>) =>
    setQuestions(draft.questions.map((q, i) => (i === index ? { ...q, ...patch } : q)));

  const changeQuestionType = (index: number, newType: string) => {
    if (isCustomQuestionType(newType)) {
      // Switching to CUSTOM: keep any author-typed fields the row already has.
      updateQuestion(index, { type: 'CUSTOM' });
    } else {
      // Prefilled types must OMIT key/label/inline_context/options (Meta rejects them).
      updateQuestion(index, { type: newType, key: null, label: null, inlineContext: null, options: null });
    }
  };

  const addQuestion = () => setQuestions([...draft.questions, emptyCustomQuestion()]);
  const removeQuestion = (index: number) => setQuestions(draft.questions.filter((_, i) => i !== index));
  const moveQuestion = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    const next = [...draft.questions];
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    setQuestions(next);
  };

  // CUSTOM label change auto-suggests the API name (field key) while it's still
  // empty; once the AM edits the key, label edits no longer clobber it.
  const onCustomLabelChange = (index: number, label: string) => {
    const q = draft.questions[index];
    if (!q) return;
    const patch: Partial<LeadFormQuestion> = { label: label || null };
    if (!q.key && label.trim()) patch.key = slugifyLeadFormKey(label);
    updateQuestion(index, patch);
  };

  const toggleMultipleChoice = (index: number) => {
    const q = draft.questions[index];
    if (!q) return;
    updateQuestion(index, { options: q.options ? null : [{ key: null, value: null }] });
  };
  const addOption = (qi: number) =>
    updateQuestion(qi, { options: [...(draft.questions[qi]?.options ?? []), { key: null, value: null }] });
  const removeOption = (qi: number, oi: number) =>
    updateQuestion(qi, { options: (draft.questions[qi]?.options ?? []).filter((_, i) => i !== oi) });
  const setOptionValue = (qi: number, oi: number, value: string) => {
    const opts = (draft.questions[qi]?.options ?? []).map((o, i) => {
      if (i !== oi) return o;
      const next = { ...o, value: value || null };
      if (!o.key && value.trim()) next.key = slugifyLeadFormKey(value);
      return next;
    });
    updateQuestion(qi, { options: opts });
  };
  const setOptionKey = (qi: number, oi: number, key: string) =>
    updateQuestion(qi, {
      options: (draft.questions[qi]?.options ?? []).map((o, i) =>
        i === oi ? { ...o, key: key || null } : o,
      ),
    });

  // ---------- thank-you updater ----------
  const setThankYou = (patch: Partial<LeadFormThankYou>) =>
    setDraft((d) => ({ ...d, thankYou: { ...(d.thankYou ?? emptyThankYou()), ...patch } }));
  const ty = draft.thankYou;
  const buttonTypeOptions = ty?.buttonType && !THANK_YOU_BUTTON_TYPES.includes(ty.buttonType)
    ? [ty.buttonType, ...THANK_YOU_BUTTON_TYPES]
    : THANK_YOU_BUTTON_TYPES;

  const higherIntent = draft.isOptimizedForQuality === true;

  const problems = validateCreateLeadFormInput(draft);
  const serverError =
    create.error instanceof ApiError
      ? create.error.message
      : create.error
        ? 'Could not create the lead form. Please try again.'
        : null;

  const submit = () => {
    if (create.isPending) return; // guard the non-idempotent create against double-submit
    setShowErrors(true);
    if (problems.length > 0) return;
    create.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onOpenChange(false) : undefined)}>
      <DialogContent className="flex max-h-[88vh] max-w-lg flex-col gap-0">
        <DialogHeader>
          <DialogTitle>{mode === 'duplicate' ? 'Duplicate lead form' : 'Create lead form'}</DialogTitle>
          <DialogDescription>
            {mode === 'duplicate'
              ? 'Tweak and create a copy on this Page. Questions, the thank-you screen, the form type and the intro card are carried over from the source form.'
              : 'Create a new Lead Gen form on this Page and use it for this ad.'}
          </DialogDescription>
        </DialogHeader>

        <div className="-mr-2 flex-1 space-y-6 overflow-y-auto py-4 pr-2">
          {/* Name */}
          <div>
            <label htmlFor="lf-name" className={labelCls}>
              Form name
            </label>
            <input
              id="lf-name"
              type="text"
              value={draft.name}
              autoFocus
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="e.g. Mt Druitt — EOFY lead form"
              className={inputCls}
            />
          </div>

          {/* Form type (isOptimizedForQuality) */}
          <fieldset>
            <legend className={labelCls}>Form type</legend>
            <div className="space-y-1.5">
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="lf-form-type"
                  checked={!higherIntent}
                  onChange={() => setDraft((d) => ({ ...d, isOptimizedForQuality: null }))}
                  className="mt-0.5 h-4 w-4 border-slate-300 text-kyck-600 focus:ring-kyck-500"
                />
                <span>
                  <span className="font-medium">Open</span> (more volume)
                  <span className="block text-xs text-slate-400">
                    More leads, lower intent — the standard form.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="lf-form-type"
                  checked={higherIntent}
                  onChange={() => setDraft((d) => ({ ...d, isOptimizedForQuality: true }))}
                  className="mt-0.5 h-4 w-4 border-slate-300 text-kyck-600 focus:ring-kyck-500"
                />
                <span>
                  <span className="font-medium">Higher intent</span> (restricted)
                  <span className="block text-xs text-slate-400">
                    Adds a review step — fewer, higher-quality leads.
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          {/* Questions builder */}
          <div>
            <span className={labelCls}>Questions</span>
            <div className="space-y-3">
              {draft.questions.map((q, i) => {
                const custom = isCustomQuestionType(q.type);
                return (
                  <div key={i} className="rounded-md border border-slate-200 p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col">
                        <button
                          type="button"
                          aria-label="Move question up"
                          disabled={i === 0}
                          onClick={() => moveQuestion(i, -1)}
                          className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                        >
                          <ChevronUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          aria-label="Move question down"
                          disabled={i === draft.questions.length - 1}
                          onClick={() => moveQuestion(i, 1)}
                          className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <select
                        aria-label={`Question ${i + 1} type`}
                        value={custom ? 'CUSTOM' : q.type.toUpperCase()}
                        onChange={(e) => changeQuestionType(i, e.target.value)}
                        className={`${inputCls} flex-1`}
                      >
                        {/* If the source form used a prefilled type outside our list,
                            keep it selectable so duplicate doesn't silently drop it. */}
                        {!custom && !QUESTION_TYPE_OPTIONS.includes(q.type.toUpperCase()) && (
                          <option value={q.type.toUpperCase()}>{leadFormQuestionTypeLabel(q.type)}</option>
                        )}
                        {QUESTION_TYPE_OPTIONS.map((t) => (
                          <option key={t} value={t}>
                            {leadFormQuestionTypeLabel(t)}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        aria-label="Remove question"
                        onClick={() => removeQuestion(i)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {custom && (
                      <div className="mt-3 space-y-2.5 border-t border-slate-100 pt-3">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-slate-600">
                            Question (lead-facing) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={q.label ?? ''}
                            onChange={(e) => onCustomLabelChange(i, e.target.value)}
                            placeholder="e.g. What is your main goal?"
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-slate-600">
                            API name / field key <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={q.key ?? ''}
                            onChange={(e) => updateQuestion(i, { key: e.target.value || null })}
                            placeholder="e.g. main_goal"
                            className={`${inputCls} font-mono`}
                          />
                          <p className="mt-1 text-xs text-slate-400">
                            How this answer maps into the CRM / webhook. No spaces.
                          </p>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-slate-600">
                            Helper text (optional)
                          </label>
                          <input
                            type="text"
                            value={q.inlineContext ?? ''}
                            onChange={(e) => updateQuestion(i, { inlineContext: e.target.value || null })}
                            placeholder="Shown under the question"
                            className={inputCls}
                          />
                        </div>

                        <label className="flex items-center gap-2 text-xs text-slate-700">
                          <input
                            type="checkbox"
                            checked={q.options != null}
                            onChange={() => toggleMultipleChoice(i)}
                            className="h-4 w-4 rounded border-slate-300 text-kyck-600 focus:ring-kyck-500"
                          />
                          Multiple choice
                        </label>

                        {q.options != null && (
                          <div className="space-y-2 rounded-md bg-slate-50 p-2.5">
                            {q.options.map((o, oi) => (
                              <div key={oi} className="flex items-center gap-2">
                                <input
                                  type="text"
                                  aria-label={`Option ${oi + 1} label`}
                                  value={o.value ?? ''}
                                  onChange={(e) => setOptionValue(i, oi, e.target.value)}
                                  placeholder="Displayed option"
                                  className={`${inputCls} flex-1`}
                                />
                                <input
                                  type="text"
                                  aria-label={`Option ${oi + 1} stored value`}
                                  value={o.key ?? ''}
                                  onChange={(e) => setOptionKey(i, oi, e.target.value)}
                                  placeholder="stored_value"
                                  className={`${inputCls} w-32 font-mono`}
                                />
                                <button
                                  type="button"
                                  aria-label="Remove option"
                                  onClick={() => removeOption(i, oi)}
                                  className="text-slate-400 hover:text-slate-600"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addOption(i)}
                              className="text-xs font-medium text-kyck-600 hover:text-kyck-700"
                            >
                              + Add option
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={addQuestion}
              className="mt-2.5 inline-flex items-center gap-1 text-sm font-medium text-kyck-600 hover:text-kyck-700"
            >
              <Plus className="h-4 w-4" /> Add question
            </button>
          </div>

          {/* Privacy policy URL (required) + link text */}
          <div className="space-y-2.5">
            <div>
              <label htmlFor="lf-privacy" className={labelCls}>
                Privacy policy URL <span className="text-red-500">*</span>
              </label>
              <input
                id="lf-privacy"
                type="url"
                value={draft.privacyPolicyUrl}
                onChange={(e) => setDraft((d) => ({ ...d, privacyPolicyUrl: e.target.value }))}
                placeholder="https://example.com/privacy"
                className={inputCls}
              />
              <p className="mt-1 text-xs text-slate-400">Required by Meta. Must be a full https:// URL.</p>
            </div>
            <div>
              <label htmlFor="lf-privacy-text" className={labelCls}>
                Privacy policy link text (optional)
              </label>
              <input
                id="lf-privacy-text"
                type="text"
                value={draft.privacyPolicyLinkText ?? ''}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, privacyPolicyLinkText: e.target.value || null }))
                }
                placeholder="e.g. Our privacy policy"
                className={inputCls}
              />
            </div>
          </div>

          {/* Thank-you screen */}
          <fieldset className="space-y-2.5">
            <legend className={labelCls}>Thank-you screen (optional)</legend>
            <input
              type="text"
              aria-label="Thank-you title"
              value={ty?.title ?? ''}
              onChange={(e) => setThankYou({ title: e.target.value || null })}
              placeholder="Title — e.g. Thanks!"
              className={inputCls}
            />
            <textarea
              aria-label="Thank-you body"
              value={ty?.body ?? ''}
              onChange={(e) => setThankYou({ body: e.target.value || null })}
              placeholder="Body — e.g. We'll be in touch shortly."
              rows={2}
              className={inputCls}
            />
            <div>
              <label htmlFor="lf-ty-button-type" className="mb-1 block text-xs font-medium text-slate-600">
                Button type
              </label>
              <select
                id="lf-ty-button-type"
                aria-label="Thank-you button type"
                value={ty?.buttonType ?? ''}
                onChange={(e) => setThankYou({ buttonType: e.target.value || null })}
                className={`${inputCls} w-40`}
              >
                <option value="">No button</option>
                {buttonTypeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="lf-ty-button-text" className="mb-1 block text-xs font-medium text-slate-600">
                Button label
              </label>
              <input
                id="lf-ty-button-text"
                type="text"
                aria-label="Thank-you button text"
                value={ty?.buttonText ?? ''}
                onChange={(e) => setThankYou({ buttonText: e.target.value || null })}
                placeholder="e.g. Visit our website"
                className={inputCls}
              />
            </div>
            <input
              type="url"
              aria-label="Thank-you website URL"
              value={ty?.websiteUrl ?? ''}
              onChange={(e) => setThankYou({ websiteUrl: e.target.value || null })}
              placeholder="Button website URL (https://…)"
              className={inputCls}
            />
          </fieldset>

          {/* Optionals */}
          <fieldset className="space-y-2.5">
            <legend className={labelCls}>Other options</legend>
            <div>
              <label htmlFor="lf-followup" className="mb-1 block text-xs font-medium text-slate-600">
                Follow-up action URL (optional)
              </label>
              <input
                id="lf-followup"
                type="url"
                value={draft.followUpActionUrl ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, followUpActionUrl: e.target.value || null }))}
                placeholder="Where leads go post-submit (https://…)"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="lf-locale" className="mb-1 block text-xs font-medium text-slate-600">
                Locale (optional)
              </label>
              <input
                id="lf-locale"
                type="text"
                value={draft.locale ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, locale: e.target.value || null }))}
                placeholder="e.g. EN_US"
                className={`${inputCls} w-40 font-mono`}
              />
            </div>
          </fieldset>

          {showErrors && problems.length > 0 && (
            <ul className="space-y-1 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
              {problems.map((p) => (
                <li key={p}>• {p}</li>
              ))}
            </ul>
          )}
          {serverError && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{serverError}</p>
          )}
        </div>

        <DialogFooter className="border-t border-slate-100 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : mode === 'duplicate' ? 'Create copy' : 'Create form'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
